import { createSelector } from '@ngrx/store';
import * as fromRouter from '~core/store';
import { AttributeOption, PriceFilterRange, RangeFilter } from '~shared/models';
import { Attribute, Tag } from '~app/modules/filters/models';
import { Category } from '~app/modules/generic/models';
import isEmpty from 'lodash.isempty';
import { getAttributes, getCategories } from './filters.selectors';
import { getFilterParams, getOptionRangesFilters, getPriceFilters } from './filter-params.selectors';
import { FILTER_RANGE_KEYS } from '../../constants';
import { getLocalFilterAttributes, getLocalFilterCategories } from '../local-filters/local-filters.selectors';
import { FiltersStateUtil } from '~app/modules/filters/utils/filters-state.util';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';

export const getPriceRangeTag = createSelector(getPriceFilters, (price: PriceFilterRange): PriceFilterRange => {
  if (price && price.from !== null && price.to !== null) {
    return price;
  }

  return null;
});

export const getWidthTag = createSelector(getOptionRangesFilters, (range: RangeFilter): RangeFilter => {
  range = range[FILTER_RANGE_KEYS.WIDTH];
  if (range && range.from !== null && range.to !== null) {
    return range;
  }
  return null;
});

export const getHeightTag = createSelector(getOptionRangesFilters, (range: RangeFilter): RangeFilter => {
  range = range[FILTER_RANGE_KEYS.HEIGHT];
  if (range && range.from !== null && range.to !== null) {
    return range;
  }
  return null;
});

export const getLengthTag = createSelector(getOptionRangesFilters, (range: RangeFilter): RangeFilter => {
  range = range[FILTER_RANGE_KEYS.LENGTH];
  if (range && range.from !== null && range.to !== null) {
    return range;
  }
  return null;
});

export const getDiameterTag = createSelector(getOptionRangesFilters, (range: RangeFilter): RangeFilter => {
  range = range[FILTER_RANGE_KEYS.DIAMETER];
  if (range && range.from !== null && range.to !== null) {
    return range;
  }
  return null;
});

export const getCategoryTags = createSelector(
  getFilterParams,
  getCategories,
  getLocalFilterCategories,
  fromRouter.getRouterState,
  (paramFilters, serverCategories, localCategories, activeRoute): Tag<Category>[] => {
    if (!FiltersStateUtil.isCategoriesSelected(paramFilters) || UrlUtils.isBrandPlpPage(activeRoute?.url)) {
      return [];
    }
    const categorySlugs = FiltersStateUtil.getCategoriesSlugs(paramFilters);

    if (!categorySlugs || !categorySlugs?.length) {
      return [];
    }

    return categorySlugs
      .map((slug: string) => getSelectedCategoryComparingParentCategoriesSlugs(serverCategories, localCategories, slug))
      .filter((tag: Tag<Category>) => !isEmpty(tag.value));
  }
);

export const getBlackDealTag = createSelector(getFilterParams, (filter) => {
  const blackDeal = filter && filter.blackFriday;
  return Boolean(blackDeal);
});
export const getPromotionTag = createSelector(getFilterParams, (filter) => {
  const promotions = filter && filter.promotion;
  return Boolean(promotions);
});

export const getVolumePriceTag = createSelector(getFilterParams, (filter) => {
  const volumePrice = filter && filter.volumePrices;
  return Boolean(volumePrice);
});

export const getAttributeTags = createSelector(
  getFilterParams,
  getAttributes,
  getLocalFilterAttributes,
  (paramFilters = {}, serverAttributes = [], localAttributes = []) => {
    if (!FiltersStateUtil.isAttributesSelected(paramFilters)) {
      return [];
    }

    const attributes = FiltersStateUtil.getMergedAttributes(localAttributes, serverAttributes);
    const { options = {} } = paramFilters;
    if (isEmpty(options)) {
      return [];
    }

    const keys: string[] = Object.keys(options);
    const attributesKeys: string[] = Array.isArray(keys) ? keys : [];
    return attributesKeys
      ?.reduce((attrs, attrKey) => {
        let tag = [];
        if (!!options[`${attrKey}`]) {
          if (Array.isArray(options[`${attrKey}`])) {
            tag = options[`${attrKey}`]?.reduce((acc, attrOption) => {
              const foundAttr: Attribute = FiltersStateUtil.getAttributeWithSelectedOption(
                attributes,
                attrKey,
                attrOption
              );
              if (!foundAttr) {
                return [...acc, null];
              }

              const isServerAttribute = serverAttributes?.find((serverAttr: Attribute) => {
                const serverAttributeOptions: AttributeOption[] = serverAttr?.options || [];
                return (
                  serverAttr.id === attrKey &&
                  !!serverAttributeOptions.find((opt: AttributeOption): boolean => opt.value === attrOption.toString())
                );
              });
              return [
                ...acc,
                {
                  value: { ...foundAttr },
                  fromLocal: !isServerAttribute,
                },
              ];
            }, []);
          }
        }
        return [...attrs, ...tag];
      }, [])
      .filter((attrTags: { value: Attribute; fromLocal: boolean }) => {
        return attrTags && attrTags.value && !isEmpty(attrTags.value.options);
      });
  }
);

export function getSelectedCategoryComparingParentCategoriesSlugs(
  serverCategories,
  localCategories,
  slug
): Tag<Category> {
  const serverCategory = serverCategories.find((c: Category) => c.slug === slug);
  return {
    value: serverCategory || localCategories.find((c: Category) => c.slug === slug),
    fromLocal: !serverCategory,
  } as Tag<Category>;
}
export const isTagsVisible = createSelector(
  getPriceRangeTag,
  getPromotionTag,
  getVolumePriceTag,
  getAttributeTags,
  getCategoryTags,
  getWidthTag,
  getHeightTag,
  getLengthTag,
  (priceTags, promotionTag, volumePriceTag, attributeTags, categories, widthTag, heightTag, lengthTag) =>
    !!priceTags ||
    promotionTag ||
    volumePriceTag ||
    0 < attributeTags?.length ||
    0 < categories?.length ||
    !!widthTag ||
    !!heightTag ||
    !!lengthTag
);

export const isBlackDealTagVisible = createSelector(getBlackDealTag, (blackDealTag) => blackDealTag);
